import { translate } from '@jsverse/transloco';
import {
  TourItinerary,
  TourService,
  TourStopBillboard,
  TourStopPoi,
} from '@tmx/modus-operandi';

import { allTasksCompleteBillboardTemplate } from './models/tour-stops/all-tasks-complete-billboard-template';
import { tellUsAboutYourselfBillboardTemplate } from './models/tour-stops/tell-us-about-yourself-billboard-template';
import { welcomeToAccountManagerBillboardTemplate } from './models/tour-stops/welcome-to-account-manager-billboard-template';

export function createWelcomeTourItinerary(tourService: TourService) {
  let setupChoice: '' | 'no' | 'yes' = '';

  return new TourItinerary(
    translate('accountManagerTour.welcomeTour.title'),
    [
      new TourStopBillboard(
        welcomeToAccountManagerBillboardTemplate,
        {
          title: translate('accountManagerTour.welcomeTour.title'),
          subtitle: translate('accountManagerTour.welcomeTour.subtitle'),
          feature1Emphasis: translate(
            'accountManagerTour.welcomeTour.feature1Emphasis'
          ),
          feature1Regular: translate(
            'accountManagerTour.welcomeTour.feature1Regular'
          ),
          feature2RegularPrefix: translate(
            'accountManagerTour.welcomeTour.feature2RegularPrefix'
          ),
          feature2Emphasis: translate(
            'accountManagerTour.welcomeTour.feature2Emphasis'
          ),
          feature2RegularSuffix: translate(
            'accountManagerTour.welcomeTour.feature2RegularSuffix'
          ),
          feature3Emphasis: translate(
            'accountManagerTour.welcomeTour.feature3Emphasis'
          ),
          feature3Regular: translate(
            'accountManagerTour.welcomeTour.feature3Regular'
          ),
        },
        {
          nextButton: {
            displayText: translate('accountManagerTour.welcomeTour.getStarted'),
            listener: () => {
              tourService.showTourProgressCard.set(true);
              tourService.nextStop();
            },
          },
        }
      ),
      new TourStopBillboard(
        tellUsAboutYourselfBillboardTemplate,
        {
          title: translate('accountManagerTour.welcomeTour.tellUsTitle'),
          setupQuestion: translate(
            'accountManagerTour.welcomeTour.setupQuestion'
          ),
          disclaimer: translate('accountManagerTour.welcomeTour.disclaimer'),
          imageAltText: translate(
            'accountManagerTour.welcomeTour.imageAltText'
          ),
        },
        {
          selectYesButton: {
            displayText: translate('accountManagerTour.welcomeTour.yes'),
            listener: ({ selectYesButton, selectNoButton, nextButton }) => {
              setupChoice = 'yes';
              selectYesButton.classList.add('active');
              selectNoButton.classList.remove('active');
              nextButton.removeAttribute('disabled');
            },
          },
          selectNoButton: {
            displayText: translate('accountManagerTour.welcomeTour.no'),
            listener: ({ selectYesButton, selectNoButton, nextButton }) => {
              setupChoice = 'no';
              selectYesButton.classList.remove('active');
              selectNoButton.classList.add('active');
              nextButton.removeAttribute('disabled');
            },
          },
          nextButton: {
            displayText: translate('accountManagerTour.welcomeTour.continue'),
            listener: () => {
              if (setupChoice === 'yes') {
                tourService.nextStop();
                tourService.logBrazeCustomEvent('onboardingtour_started');
              } else if (setupChoice === 'no') {
                tourService.endTour();
                tourService.logBrazeCustomEvent('onboardingtour_dismissed');
              }
            },
          },
          skipButton: {
            displayText: translate('accountManagerTour.welcomeTour.skip'),
            listener: () => {
              tourService.endTour();
              tourService.logBrazeCustomEvent('onboardingtour_skipped');
            },
          },
        }
      ),
      new TourStopPoi(
        '[data-tour-poi="tourProgress"]',
        translate('accountManagerTour.welcomeTour.onboardingTooltipTitle'),
        translate('accountManagerTour.welcomeTour.onboardingTooltipBody'),
        {
          nextButton: {
            displayText: translate('accountManagerTour.welcomeTour.gotIt'),
            listener: () => {
              tourService.completeItineraryByName('Welcome to Account Manager');
              tourService.pauseTour();
            },
          },
        },
        { side: 'top', align: 'center' }
      ),
    ],
    undefined,
    false
  );
}

export function createAssetsTourItinerary(tourService: TourService) {
  return new TourItinerary(
    translate('accountManagerTour.assetsTour.itineraryTitle'),
    [
      new TourStopPoi(
        '[data-tour-poi="addAssetButtons"]',
        translate('accountManagerTour.assetsTour.addAssetsTitle'),
        translate('accountManagerTour.assetsTour.addAssetsBody'),
        {
          nextButton: {
            displayText: translate('accountManagerTour.assetsTour.gotIt'),
            listener: () => {
              tourService.pauseTour();
            },
          },
          endButton: {
            displayText: translate('accountManagerTour.assetsTour.skip'),
            listener: () => {
              tourService.skipItinerary();
            },
          },
        }
      ),
      new TourStopPoi(
        '[data-tour-poi="assignLicensesTab"]',
        translate('accountManagerTour.assetsTour.selectLicenseTitle'),
        translate('accountManagerTour.assetsTour.selectLicenseBody'),
        {
          nextButton: {
            displayText: translate('accountManagerTour.assetsTour.gotIt'),
            listener: () => {
              tourService.pauseTour();
            },
          },
          endButton: {
            displayText: translate('accountManagerTour.assetsTour.skip'),
            listener: () => {
              tourService.skipItinerary();
            },
          },
        }
      ),
      new TourStopPoi(
        '[data-tour-poi="assetsDataTable"]',
        translate('accountManagerTour.assetsTour.selectAssetsTitle'),
        '',
        {
          nextButton: {
            displayText: translate('accountManagerTour.assetsTour.gotIt'),
            listener: () => {
              tourService.pauseTour();
            },
          },
          endButton: {
            displayText: translate('accountManagerTour.assetsTour.skip'),
            listener: () => {
              tourService.skipItinerary();
            },
          },
        }
      ),
      new TourStopPoi(
        '[data-tour-poi="assetsAssignButton"]',
        translate('accountManagerTour.assetsTour.assignButtonTitle'),
        '',
        {
          nextButton: {
            displayText: translate('accountManagerTour.assetsTour.gotIt'),
            listener: () => {
              tourService.pauseTour();
            },
          },
          endButton: {
            displayText: translate('accountManagerTour.assetsTour.skip'),
            listener: () => {
              tourService.skipItinerary();
            },
          },
        }
      ),
    ],
    'ams/assets'
  );
}

export function createVehicleGroupTourItinerary(tourService: TourService) {
  return new TourItinerary(
    translate('accountManagerTour.vehicleGroupsTour.itineraryTitle'),
    [
      new TourStopPoi(
        '[data-tour-poi="addVehicleGroupButton"]',
        translate(
          'accountManagerTour.vehicleGroupsTour.createVehicleGroupTitle'
        ),
        translate(
          'accountManagerTour.vehicleGroupsTour.createVehicleGroupBody'
        ),
        {
          nextButton: {
            displayText: translate(
              'accountManagerTour.vehicleGroupsTour.gotIt'
            ),
            listener: () => {
              tourService.pauseTour();
            },
          },
          endButton: {
            displayText: translate('accountManagerTour.vehicleGroupsTour.skip'),
            listener: () => {
              tourService.skipItinerary();
            },
          },
        }
      ),
    ],
    'ams/cfs/vehicle-groups'
  );
}

export function createRoutingProfileTourItinerary(tourService: TourService) {
  return new TourItinerary(
    translate('accountManagerTour.routingProfilesTour.itineraryTitle'),
    [
      new TourStopPoi(
        '[data-tour-poi="addRoutingProfileButton"]',
        translate(
          'accountManagerTour.routingProfilesTour.createRoutingProfileTitle'
        ),
        translate(
          'accountManagerTour.routingProfilesTour.createRoutingProfileBody'
        ),
        {
          nextButton: {
            displayText: translate(
              'accountManagerTour.routingProfilesTour.gotIt'
            ),
            listener: () => {
              tourService.pauseTour();
            },
          },
          endButton: {
            displayText: translate(
              'accountManagerTour.routingProfilesTour.skip'
            ),
            listener: () => {
              tourService.skipItinerary();
            },
          },
        }
      ),
    ],
    'ams/cfs/routing-profiles'
  );
}

export function createMapSetTourItinerary(tourService: TourService) {
  return new TourItinerary(
    translate('accountManagerTour.mapSetTour.itineraryTitle'),
    [
      new TourStopPoi(
        '[data-tour-poi="addMapSetButton"]',
        translate('accountManagerTour.mapSetTour.createMapSetTitle'),
        translate('accountManagerTour.mapSetTour.createMapSetBody'),
        {
          nextButton: {
            displayText: translate('accountManagerTour.mapSetTour.gotIt'),
            listener: () => {
              tourService.pauseTour();
            },
          },
          endButton: {
            displayText: translate('accountManagerTour.mapSetTour.skip'),
            listener: () => {
              tourService.skipItinerary();
            },
          },
        }
      ),
      new TourStopPoi(
        '[data-tour-poi="assignMapSetTable"]',
        translate('accountManagerTour.mapSetTour.assignToVehicleGroupTitle'),
        translate('accountManagerTour.mapSetTour.assignToVehicleGroupBody'),
        {
          nextButton: {
            displayText: translate('accountManagerTour.mapSetTour.gotIt'),
            listener: () => {
              tourService.pauseTour();
            },
          },
          endButton: {
            displayText: translate('accountManagerTour.mapSetTour.skip'),
            listener: () => {
              tourService.skipItinerary();
            },
          },
        },
        { side: 'bottom', align: 'start' }
      ),
    ],
    'ams/cfs/map-sets'
  );
}

export function createInviteDriversTourItinerary(tourService: TourService) {
  return new TourItinerary(
    translate('accountManagerTour.inviteDriversTour.itineraryTitle'),
    [
      new TourStopPoi(
        'app-invite-viewer',
        translate('accountManagerTour.inviteDriversTour.inviteContactTitle'),
        translate('accountManagerTour.inviteDriversTour.inviteContactBody'),
        {
          nextButton: {
            displayText: translate(
              'accountManagerTour.inviteDriversTour.gotIt'
            ),
            listener: () => {
              tourService.pauseTour();
            },
          },
          endButton: {
            displayText: translate('accountManagerTour.inviteDriversTour.skip'),
            listener: () => {
              tourService.skipItinerary();
            },
          },
        }
      ),
    ],
    'ams/assets'
  );
}

export function createAllTasksCompleteTourItinerary(tourService: TourService) {
  return new TourItinerary(
    translate('accountManagerTour.allTasksCompleteTour.itineraryTitle'),
    [
      new TourStopBillboard(
        allTasksCompleteBillboardTemplate,
        {
          title: translate('accountManagerTour.allTasksCompleteTour.title'),
          subtitle: translate(
            'accountManagerTour.allTasksCompleteTour.subtitle'
          ),
          card1Title: translate(
            'accountManagerTour.allTasksCompleteTour.card1Title'
          ),
          card1Text: translate(
            'accountManagerTour.allTasksCompleteTour.card1Text'
          ),
          card2Title: translate(
            'accountManagerTour.allTasksCompleteTour.card2Title'
          ),
          card2Text: translate(
            'accountManagerTour.allTasksCompleteTour.card2Text'
          ),
          card3Title: translate(
            'accountManagerTour.allTasksCompleteTour.card3Title'
          ),
          card3Text: translate(
            'accountManagerTour.allTasksCompleteTour.card3Text'
          ),
          card4Title: translate(
            'accountManagerTour.allTasksCompleteTour.card4Title'
          ),
          card4Text: translate(
            'accountManagerTour.allTasksCompleteTour.card4Text'
          ),
        },
        {
          nextButton: {
            displayText: translate(
              'accountManagerTour.allTasksCompleteTour.finish'
            ),
            listener: () => {
              tourService.endTour();
              tourService.logBrazeCustomEvent('onboardingtour_completed');
            },
          },
        }
      ),
    ],
    undefined,
    false
  );
}
